import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { Table, Card, Tag, Typography, Row, Col, Progress, Tooltip, Button, Modal, Descriptions, Empty, Badge } from 'antd';
import {
  WarningOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ExclamationCircleOutlined,
  ReloadOutlined,
  EyeOutlined,
  BankOutlined,
  AlertOutlined,
  SafetyCertificateOutlined,
  DollarOutlined,
} from '@ant-design/icons';
import StatCard from '../components/StatCard';
import { providersAPI, servicesAPI, bookingsAPI, paymentsAPI, availabilitySlotsAPI } from '../api/services';

const { Title, Text } = Typography;

// Risk level thresholds
const RISK_LEVELS = {
  HIGH: { color: '#ff4d4f', label: 'Yuqori xavf', tag: 'error', min: 60 },
  MEDIUM: { color: '#fa8c16', label: "O'rta xavf", tag: 'warning', min: 30 },
  LOW: { color: '#52c41a', label: 'Past xavf', tag: 'success', min: 0 },
};

const getRiskLevel = (score) => {
  if (score >= RISK_LEVELS.HIGH.min) return RISK_LEVELS.HIGH;
  if (score >= RISK_LEVELS.MEDIUM.min) return RISK_LEVELS.MEDIUM;
  return RISK_LEVELS.LOW;
};

// Risk indicator calculations
const calcPaymentGap = (providerBookings, allPayments) => {
  const totalBookingAmount = providerBookings.reduce((s, b) => s + Number(b.total_amount || 0), 0);
  if (totalBookingAmount === 0) return { score: 0, percent: 0, bookingTotal: 0, paymentTotal: 0 };

  const bookingIds = new Set(providerBookings.map(b => b.id));
  const provPayments = allPayments.filter(p => bookingIds.has(p.booking_id) && p.status === 'paid');
  const totalPaid = provPayments.reduce((s, p) => s + Number(p.amount || 0), 0);

  const gapPercent = ((totalBookingAmount - totalPaid) / totalBookingAmount) * 100;
  const score = gapPercent > 70 ? 25 : gapPercent > 50 ? 18 : gapPercent > 30 ? 10 : gapPercent > 10 ? 5 : 0;

  return { score, percent: Math.round(gapPercent), bookingTotal: totalBookingAmount, paymentTotal: totalPaid };
};

const calcPriceManipulation = (providerServices, providerBookings) => {
  if (providerServices.length === 0 || providerBookings.length === 0) return { score: 0, percent: 0, details: [] };

  const details = [];
  let totalDiffPercent = 0;
  let count = 0;

  providerServices.forEach(svc => {
    const svcPrice = Number(svc.price || 0);
    if (svcPrice === 0) return;
    const svcBookings = providerBookings.filter(b => b.service_id === svc.id);
    if (svcBookings.length === 0) return;

    const avgBookingPrice = svcBookings.reduce((s, b) => s + Number(b.price || 0), 0) / svcBookings.length;
    const diffPercent = ((svcPrice - avgBookingPrice) / svcPrice) * 100;

    if (diffPercent > 10) {
      details.push({ service: svc.title, catalogPrice: svcPrice, avgBooking: Math.round(avgBookingPrice), diff: Math.round(diffPercent) });
      totalDiffPercent += diffPercent;
      count++;
    }
  });

  const avgDiff = count > 0 ? totalDiffPercent / count : 0;
  const score = avgDiff > 50 ? 20 : avgDiff > 30 ? 14 : avgDiff > 15 ? 8 : 0;

  return { score, percent: Math.round(avgDiff), details };
};

const calcAvailabilityBookingGap = (providerId, providerServiceIds, slots, providerBookings) => {
  const provSlots = (slots || []).filter(s => s.provider_id === providerId || providerServiceIds.includes(s.service_id));
  const busyDays = new Set(provSlots.map(s => Number(s.day))).size;

  if (busyDays === 0) return { score: 0, busyDays: 0, bookingCount: providerBookings.length, ratio: 0 };

  // If many busy days but very few bookings — suspicious
  const expectedMinBookings = busyDays * 2; // at least 2 bookings per busy day
  const ratio = providerBookings.length > 0 ? (providerBookings.length / expectedMinBookings) * 100 : 0;
  const gapScore = busyDays >= 5 && providerBookings.length < 3 ? 20 :
                   busyDays >= 3 && providerBookings.length < 2 ? 12 : 0;

  return { score: gapScore, busyDays, bookingCount: providerBookings.length, ratio: Math.round(ratio) };
};

const calcStirCheck = (provider) => {
  const stir = provider.stir;
  const hasStir = stir && stir !== 0 && stir !== '0' && String(stir).length >= 9;
  return { score: hasStir ? 0 : 15, hasStir, stir: stir || 0 };
};

const calcExpenseRatio = (providerBookings) => {
  // We don't have direct expense data per provider from API list,
  // but we can check cancelled/refunded ratio as indirect indicator
  const total = providerBookings.length;
  if (total === 0) return { score: 0, cancelRate: 0 };

  const cancelled = providerBookings.filter(b => b.status === 'cancelled').length;
  const cancelRate = (cancelled / total) * 100;
  const score = cancelRate > 50 ? 15 : cancelRate > 30 ? 10 : cancelRate > 15 ? 5 : 0;

  return { score, cancelRate: Math.round(cancelRate), cancelled, total };
};

const calcOverallRisk = (indicators) => {
  return Object.values(indicators).reduce((sum, ind) => sum + (ind.score || 0), 0);
};

const TaxAnalysisPage = () => {
  const [loading, setLoading] = useState(true);
  const [providers, setProviders] = useState([]);
  const [services, setServices] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [payments, setPayments] = useState([]);
  const [slots, setSlots] = useState([]);
  const [detailModal, setDetailModal] = useState({ open: false, data: null });

  const fetchAllData = useCallback(async () => {
    setLoading(true);
    try {
      const [provRes, svcRes, bookRes, payRes, slotRes] = await Promise.all([
        providersAPI.getAll({ per_page: 500 }),
        servicesAPI.getAll({ per_page: 500 }),
        bookingsAPI.getAll({ per_page: 1000 }),
        paymentsAPI.getAll({ per_page: 1000 }),
        availabilitySlotsAPI.getAll({ per_page: 1000 }),
      ]);

      const extract = (res) => {
        const d = res.data?.data || res.data;
        return Array.isArray(d) ? d : d?.data || [];
      };

      setProviders(extract(provRes));
      setServices(extract(svcRes));
      setBookings(extract(bookRes));
      setPayments(extract(payRes));
      setSlots(extract(slotRes));
    } catch (err) {
      console.error('Tax analysis data fetch error:', err);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchAllData();
  }, [fetchAllData]);

  // Compute risk analysis per provider
  const analysisData = useMemo(() => {
    return providers.map(provider => {
      const provServices = services.filter(s => s.provider_id === provider.id);
      const provServiceIds = provServices.map(s => s.id);
      const provBookings = bookings.filter(b =>
        b.provider_id === provider.id || provServiceIds.includes(b.service_id)
      );

      const paymentGap = calcPaymentGap(provBookings, payments);
      const priceManip = calcPriceManipulation(provServices, provBookings);
      const availGap = calcAvailabilityBookingGap(provider.id, provServiceIds, slots, provBookings);
      const stirCheck = calcStirCheck(provider);
      const expenseRatio = calcExpenseRatio(provBookings);

      const indicators = { paymentGap, priceManip, availGap, stirCheck, expenseRatio };
      const totalScore = calcOverallRisk(indicators);
      const riskLevel = getRiskLevel(totalScore);

      return {
        key: provider.id,
        provider,
        provServices,
        provBookings,
        indicators,
        totalScore,
        riskLevel,
        serviceCount: provServices.length,
        bookingCount: provBookings.length,
        totalRevenue: provBookings.reduce((s, b) => s + Number(b.total_amount || 0), 0),
      };
    }).sort((a, b) => b.totalScore - a.totalScore);
  }, [providers, services, bookings, payments, slots]);

  // Summary stats
  const stats = useMemo(() => {
    const high = analysisData.filter(d => d.totalScore >= RISK_LEVELS.HIGH.min).length;
    const medium = analysisData.filter(d => d.totalScore >= RISK_LEVELS.MEDIUM.min && d.totalScore < RISK_LEVELS.HIGH.min).length;
    const noStir = analysisData.filter(d => !d.indicators.stirCheck.hasStir).length;
    const totalRevenue = analysisData.reduce((s, d) => s + d.totalRevenue, 0);
    return { high, medium, noStir, totalRevenue };
  }, [analysisData]);

  const columns = [
    {
      title: '#',
      key: 'index',
      width: 50,
      render: (_, __, i) => i + 1,
    },
    {
      title: 'Provayder',
      key: 'name',
      ellipsis: true,
      render: (_, r) => (
        <div>
          <Text strong>{r.provider.name}</Text>
          <div style={{ fontSize: 12, color: '#888' }}>{r.provider.address || 'Manzil yo\'q'}</div>
        </div>
      ),
    },
    {
      title: 'STIR',
      key: 'stir',
      width: 120,
      render: (_, r) => r.indicators.stirCheck.hasStir
        ? <Tag color="green">{r.provider.stir}</Tag>
        : <Tag color="red" icon={<CloseCircleOutlined />}>Yo'q</Tag>,
    },
    {
      title: 'Xizmatlar',
      key: 'services',
      width: 80,
      align: 'center',
      render: (_, r) => r.serviceCount,
      sorter: (a, b) => a.serviceCount - b.serviceCount,
    },
    {
      title: 'Buyurtmalar',
      key: 'bookings',
      width: 90,
      align: 'center',
      render: (_, r) => r.bookingCount,
      sorter: (a, b) => a.bookingCount - b.bookingCount,
    },
    {
      title: "To'lov farqi",
      key: 'paymentGap',
      width: 120,
      render: (_, r) => {
        const gap = r.indicators.paymentGap;
        const color = gap.percent > 50 ? '#ff4d4f' : gap.percent > 30 ? '#fa8c16' : '#52c41a';
        return (
          <Tooltip title={`Buyurtma: ${gap.bookingTotal.toLocaleString()} / To'langan: ${gap.paymentTotal.toLocaleString()}`}>
            <Text style={{ color, fontWeight: 600 }}>{gap.percent}%</Text>
          </Tooltip>
        );
      },
      sorter: (a, b) => a.indicators.paymentGap.percent - b.indicators.paymentGap.percent,
    },
    {
      title: 'Narx farqi',
      key: 'priceManip',
      width: 100,
      render: (_, r) => {
        const pm = r.indicators.priceManip;
        const color = pm.percent > 30 ? '#ff4d4f' : pm.percent > 15 ? '#fa8c16' : '#52c41a';
        return <Text style={{ color, fontWeight: 600 }}>{pm.percent}%</Text>;
      },
      sorter: (a, b) => a.indicators.priceManip.percent - b.indicators.priceManip.percent,
    },
    {
      title: 'Xavf balli',
      key: 'riskScore',
      width: 140,
      render: (_, r) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Progress
            percent={Math.min(r.totalScore, 100)}
            size="small"
            strokeColor={r.riskLevel.color}
            showInfo={false}
            style={{ width: 60, margin: 0 }}
          />
          <Text strong style={{ color: r.riskLevel.color }}>{r.totalScore}</Text>
        </div>
      ),
      sorter: (a, b) => a.totalScore - b.totalScore,
      defaultSortOrder: 'descend',
    },
    {
      title: 'Xavf',
      key: 'riskLevel',
      width: 120,
      render: (_, r) => (
        <Tag color={r.riskLevel.tag} icon={
          r.riskLevel === RISK_LEVELS.HIGH ? <CloseCircleOutlined /> :
          r.riskLevel === RISK_LEVELS.MEDIUM ? <ExclamationCircleOutlined /> :
          <CheckCircleOutlined />
        }>
          {r.riskLevel.label}
        </Tag>
      ),
      filters: [
        { text: 'Yuqori xavf', value: 'HIGH' },
        { text: "O'rta xavf", value: 'MEDIUM' },
        { text: 'Past xavf', value: 'LOW' },
      ],
      onFilter: (value, r) => {
        if (value === 'HIGH') return r.totalScore >= RISK_LEVELS.HIGH.min;
        if (value === 'MEDIUM') return r.totalScore >= RISK_LEVELS.MEDIUM.min && r.totalScore < RISK_LEVELS.HIGH.min;
        return r.totalScore < RISK_LEVELS.MEDIUM.min;
      },
    },
    {
      title: '',
      key: 'actions',
      width: 50,
      render: (_, r) => (
        <Button type="text" icon={<EyeOutlined />} onClick={() => setDetailModal({ open: true, data: r })} />
      ),
    },
  ];

  const detailData = detailModal.data;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 8 }}>
        <div>
          <Title level={3} style={{ margin: 0 }}>Soliqdan qochish tahlili</Title>
          <Text type="secondary">Xizmat ko'rsatuvchilarning soliq xavfi tahlili</Text>
        </div>
        <Button icon={<ReloadOutlined />} onClick={fetchAllData} loading={loading}>Yangilash</Button>
      </div>

      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={24} sm={12} lg={6}>
          <StatCard title="Yuqori xavfli" value={stats.high} icon={<AlertOutlined />} color="#ff4d4f" loading={loading} />
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <StatCard title="O'rta xavfli" value={stats.medium} icon={<WarningOutlined />} color="#fa8c16" loading={loading} />
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <StatCard title="STIR yo'q" value={stats.noStir} icon={<BankOutlined />} color="#722ed1" loading={loading} />
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <StatCard title="Jami daromad" value={stats.totalRevenue} suffix="UZS" icon={<DollarOutlined />} color="#13c2c2" loading={loading} />
        </Col>
      </Row>

      {/* Risk distribution bar */}
      <Card size="small" style={{ borderRadius: 12, marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
          <Text strong>Xavf taqsimoti:</Text>
          <div style={{ flex: 1, display: 'flex', borderRadius: 8, overflow: 'hidden', height: 24, minWidth: 200 }}>
            {analysisData.length > 0 ? (
              <>
                <Tooltip title={`Yuqori: ${stats.high}`}>
                  <div style={{ width: `${(stats.high / analysisData.length) * 100}%`, background: '#ff4d4f', transition: 'width 0.3s' }} />
                </Tooltip>
                <Tooltip title={`O'rta: ${stats.medium}`}>
                  <div style={{ width: `${(stats.medium / analysisData.length) * 100}%`, background: '#fa8c16', transition: 'width 0.3s' }} />
                </Tooltip>
                <Tooltip title={`Past: ${analysisData.length - stats.high - stats.medium}`}>
                  <div style={{ width: `${((analysisData.length - stats.high - stats.medium) / analysisData.length) * 100}%`, background: '#52c41a', transition: 'width 0.3s' }} />
                </Tooltip>
              </>
            ) : (
              <div style={{ width: '100%', background: '#f0f0f0' }} />
            )}
          </div>
          <div style={{ display: 'flex', gap: 12, fontSize: 12 }}>
            <span><Badge color="#ff4d4f" /> Yuqori ({stats.high})</span>
            <span><Badge color="#fa8c16" /> O'rta ({stats.medium})</span>
            <span><Badge color="#52c41a" /> Past ({analysisData.length - stats.high - stats.medium})</span>
          </div>
        </div>
      </Card>

      <Card style={{ borderRadius: 12 }}>
        <Table
          dataSource={analysisData}
          columns={columns}
          rowKey="key"
          loading={loading}
          scroll={{ x: 1000 }}
          pagination={{
            pageSize: 20,
            showSizeChanger: true,
            showTotal: (total) => `Jami: ${total} ta provayder`,
          }}
          rowClassName={(r) => r.totalScore >= RISK_LEVELS.HIGH.min ? 'risk-row-high' : ''}
        />
      </Card>

      {/* Detail Modal */}
      <Modal
        title={
          detailData ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <SafetyCertificateOutlined />
              <span>Soliq xavfi tahlili — {detailData.provider.name}</span>
            </div>
          ) : 'Tahlil'
        }
        open={detailModal.open}
        onCancel={() => setDetailModal({ open: false, data: null })}
        footer={null}
        width={700}
      >
        {detailData ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {/* Overall score */}
            <Card size="small" style={{
              borderRadius: 12,
              background: `${detailData.riskLevel.color}08`,
              borderColor: `${detailData.riskLevel.color}30`,
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <Text type="secondary">Umumiy xavf balli</Text>
                  <div style={{ fontSize: 36, fontWeight: 700, color: detailData.riskLevel.color, lineHeight: 1.2 }}>
                    {detailData.totalScore} / 100
                  </div>
                </div>
                <Tag color={detailData.riskLevel.tag} style={{ fontSize: 14, padding: '4px 12px' }}>
                  {detailData.riskLevel.label}
                </Tag>
              </div>
              <Progress
                percent={Math.min(detailData.totalScore, 100)}
                strokeColor={detailData.riskLevel.color}
                showInfo={false}
                style={{ marginTop: 8 }}
              />
            </Card>

            {/* Provider info */}
            <Descriptions size="small" bordered column={2}>
              <Descriptions.Item label="Nomi">{detailData.provider.name}</Descriptions.Item>
              <Descriptions.Item label="STIR">
                {detailData.indicators.stirCheck.hasStir
                  ? <Tag color="green">{detailData.provider.stir}</Tag>
                  : <Tag color="red">Yo'q / Noto'g'ri</Tag>
                }
              </Descriptions.Item>
              <Descriptions.Item label="Telefon">{detailData.provider.phone || '-'}</Descriptions.Item>
              <Descriptions.Item label="Manzil">{detailData.provider.address || '-'}</Descriptions.Item>
              <Descriptions.Item label="Xizmatlar soni">{detailData.serviceCount}</Descriptions.Item>
              <Descriptions.Item label="Buyurtmalar soni">{detailData.bookingCount}</Descriptions.Item>
            </Descriptions>

            {/* Risk indicators */}
            <Title level={5} style={{ margin: 0 }}>Xavf ko'rsatkichlari</Title>

            {/* 1. STIR */}
            <Card size="small" style={{ borderRadius: 8, borderLeft: `4px solid ${detailData.indicators.stirCheck.hasStir ? '#52c41a' : '#ff4d4f'}` }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <Text strong>1. STIR tekshiruvi</Text>
                  <div style={{ fontSize: 12, color: '#888' }}>
                    {detailData.indicators.stirCheck.hasStir
                      ? `STIR mavjud: ${detailData.provider.stir}`
                      : "STIR yo'q yoki noto'g'ri — rasmiy ro'yxatdan o'tmagan bo'lishi mumkin"
                    }
                  </div>
                </div>
                <Tag color={detailData.indicators.stirCheck.score > 0 ? 'red' : 'green'}>
                  +{detailData.indicators.stirCheck.score} ball
                </Tag>
              </div>
            </Card>

            {/* 2. Payment gap */}
            <Card size="small" style={{ borderRadius: 8, borderLeft: `4px solid ${detailData.indicators.paymentGap.percent > 50 ? '#ff4d4f' : detailData.indicators.paymentGap.percent > 30 ? '#fa8c16' : '#52c41a'}` }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <Text strong>2. To'lov farqi</Text>
                  <div style={{ fontSize: 12, color: '#888' }}>
                    Buyurtma summasi: {detailData.indicators.paymentGap.bookingTotal.toLocaleString()} UZS |
                    To'langan: {detailData.indicators.paymentGap.paymentTotal.toLocaleString()} UZS |
                    Farq: <Text strong style={{ color: detailData.indicators.paymentGap.percent > 50 ? '#ff4d4f' : '#fa8c16' }}>{detailData.indicators.paymentGap.percent}%</Text>
                  </div>
                </div>
                <Tag color={detailData.indicators.paymentGap.score > 15 ? 'red' : detailData.indicators.paymentGap.score > 5 ? 'orange' : 'green'}>
                  +{detailData.indicators.paymentGap.score} ball
                </Tag>
              </div>
            </Card>

            {/* 3. Price manipulation */}
            <Card size="small" style={{ borderRadius: 8, borderLeft: `4px solid ${detailData.indicators.priceManip.percent > 30 ? '#ff4d4f' : detailData.indicators.priceManip.percent > 15 ? '#fa8c16' : '#52c41a'}` }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <Text strong>3. Narx manipulyatsiyasi</Text>
                  <div style={{ fontSize: 12, color: '#888' }}>
                    Katalog narxi va buyurtma narxi o'rtasidagi o'rtacha farq: <Text strong>{detailData.indicators.priceManip.percent}%</Text>
                  </div>
                  {detailData.indicators.priceManip.details.length > 0 && (
                    <div style={{ marginTop: 4 }}>
                      {detailData.indicators.priceManip.details.map((d, i) => (
                        <Tag key={i} color="orange" style={{ marginTop: 2, fontSize: 11 }}>
                          {d.service}: {d.catalogPrice.toLocaleString()} → {d.avgBooking.toLocaleString()} ({d.diff}%)
                        </Tag>
                      ))}
                    </div>
                  )}
                </div>
                <Tag color={detailData.indicators.priceManip.score > 10 ? 'red' : detailData.indicators.priceManip.score > 0 ? 'orange' : 'green'}>
                  +{detailData.indicators.priceManip.score} ball
                </Tag>
              </div>
            </Card>

            {/* 4. Availability-booking gap */}
            <Card size="small" style={{ borderRadius: 8, borderLeft: `4px solid ${detailData.indicators.availGap.score > 10 ? '#ff4d4f' : detailData.indicators.availGap.score > 0 ? '#fa8c16' : '#52c41a'}` }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <Text strong>4. Bandlik-buyurtma nomutanosibligi</Text>
                  <div style={{ fontSize: 12, color: '#888' }}>
                    Band kunlar: {detailData.indicators.availGap.busyDays}/7 | Buyurtmalar: {detailData.indicators.availGap.bookingCount} ta
                    {detailData.indicators.availGap.score > 0 && (
                      <Text type="danger"> — Band ko'rsatilgan, lekin buyurtma kam</Text>
                    )}
                  </div>
                </div>
                <Tag color={detailData.indicators.availGap.score > 10 ? 'red' : detailData.indicators.availGap.score > 0 ? 'orange' : 'green'}>
                  +{detailData.indicators.availGap.score} ball
                </Tag>
              </div>
            </Card>

            {/* 5. Cancel rate */}
            <Card size="small" style={{ borderRadius: 8, borderLeft: `4px solid ${detailData.indicators.expenseRatio.score > 10 ? '#ff4d4f' : detailData.indicators.expenseRatio.score > 0 ? '#fa8c16' : '#52c41a'}` }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <Text strong>5. Bekor qilish darajasi</Text>
                  <div style={{ fontSize: 12, color: '#888' }}>
                    Bekor qilingan: {detailData.indicators.expenseRatio.cancelled || 0} / {detailData.indicators.expenseRatio.total || 0} ({detailData.indicators.expenseRatio.cancelRate}%)
                    {detailData.indicators.expenseRatio.cancelRate > 30 && (
                      <Text type="danger"> — Shubhali darajada yuqori</Text>
                    )}
                  </div>
                </div>
                <Tag color={detailData.indicators.expenseRatio.score > 10 ? 'red' : detailData.indicators.expenseRatio.score > 0 ? 'orange' : 'green'}>
                  +{detailData.indicators.expenseRatio.score} ball
                </Tag>
              </div>
            </Card>

          </div>
        ) : (
          <Empty description="Ma'lumot yo'q" />
        )}
      </Modal>

      <style>{`
        .risk-row-high td { background: #fff1f0 !important; }
        .risk-row-high:hover td { background: #ffccc7 !important; }
      `}</style>
    </div>
  );
};

export default TaxAnalysisPage;
